/**
 * Global, UI-independent upload task manager for the Image Asset Center
 * (VistoeraTASK-00522).
 *
 * Why a module-level manager: upload state used to live inside the upload
 * modal component, so closing the modal dropped everything still queued. Here
 * the batches / items / in-flight requests belong to this singleton, so:
 *   - closing the modal, switching 我的资产 / 团队共享 / 全部, switching 资料库
 *     or navigating inside the SPA never stops an upload;
 *   - re-opening the modal shows live progress;
 *   - a failing library-list request does not affect uploads (and vice versa).
 * It does NOT survive closing / reloading the browser tab (no persistent
 * executor exists) - a `beforeunload` guard warns while uploads are running.
 *
 * Hard rules enforced here: bounded concurrency (never "500 at once"), bounded
 * retry with exponential backoff only on transient failures, the SAME client
 * upload id on every retry (server-side idempotency => never a duplicate
 * asset), no blind retry of 401/403/validation, a Workspace-authorization
 * failure halts the batch instead of bypassing auth, and file bytes are never
 * read here (the transport streams them straight to object storage).
 *
 * Framework-agnostic (no React / antd import) so it is fully unit-testable.
 */
import { abortableSleep, isCancelledError } from '../imageAssetLoadErrors'

export type UploadItemStatus = 'queued' | 'uploading' | 'finalizing' | 'done' | 'failed' | 'cancelled'
export type FailureKind = 'network' | 'busy' | 'auth' | 'rejected'

export interface UploadItemView {
  id: string
  name: string
  relativePath: string
  size: number
  status: UploadItemStatus
  /** 0..100 of the current attempt. */
  progress: number
  attempts: number
  failureKind?: FailureKind
  /** Server-provided reason for a plain rejection (never a secret). */
  error?: string
  chainId?: string | null
}

export interface BatchPlacement {
  collectionId: string | null
  collectionName: string | null
  /** null => server default (团队共享 in a team Workspace). */
  visibility: 'workspace' | 'private' | null
}

export interface BatchCounts {
  total: number
  done: number
  failed: number
  cancelled: number
  uploading: number
  queued: number
  /** queued + uploading (+finalizing). */
  remaining: number
  /** 0..100 incl. partial progress of in-flight files. */
  percent: number
}

export interface UploadBatchView {
  id: string
  workspaceId: string
  source: 'files' | 'folder'
  label: string | null
  placement: BatchPlacement
  createdAt: number
  finishedAt: number | null
  cancelled: boolean
  haltedReason: 'auth' | null
  /** Files skipped by the local pre-check (not uploaded, not failed). */
  skippedCount: number
  items: UploadItemView[]
  counts: BatchCounts
}

export interface UploadSnapshot {
  version: number
  batches: UploadBatchView[]
}

export interface UploadTransportRequest {
  file: File
  clientId: string
  name: string
  mime: string
  size: number
}

export interface UploadTransportContext {
  scopeBaseUrl: string
  directToR2: boolean
  placement: BatchPlacement
  autoProcess: boolean
  assetSource: string
  signal: AbortSignal
  onProgress: (pct: number) => void
  onPhase: (phase: 'finalizing') => void
}

export type UploadTransport = (
  req: UploadTransportRequest,
  ctx: UploadTransportContext,
) => Promise<{ chainId: string | null }>

export interface BatchInput {
  workspaceId: string
  source: 'files' | 'folder'
  label: string | null
  files: Array<{ file: File; mime: string; relativePath: string }>
  placement: BatchPlacement
  autoProcess: boolean
  assetSource: string
  directToR2: boolean
  scopeBaseUrl: string
  skippedCount?: number
  limits: {
    concurrency: number
    retryMaxAttempts: number
    retryBaseDelayMs: number
    retryMaxDelayMs: number
  }
}

export type UploadEvent =
  | { type: 'item-done'; workspaceId: string; batchId: string }
  | { type: 'batch-finished'; workspaceId: string; batchId: string }

export interface ManagerDeps {
  transport: UploadTransport
  now?: () => number
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
  random?: () => number
  newId?: () => string
  /** Called with true while any upload is active (drives the unload guard). */
  onActiveChange?: (active: boolean) => void
  /** Coalescing window for progress-only snapshots (0 = synchronous, for tests). */
  progressEmitMs?: number
}

interface InternalItem extends UploadItemView {
  file: File | null
  mime: string
  abort: AbortController | null
}

interface InternalBatch {
  id: string
  workspaceId: string
  source: 'files' | 'folder'
  label: string | null
  placement: BatchPlacement
  createdAt: number
  finishedAt: number | null
  cancelled: boolean
  haltedReason: 'auth' | null
  skippedCount: number
  items: InternalItem[]
  autoProcess: boolean
  assetSource: string
  directToR2: boolean
  scopeBaseUrl: string
  retryMaxAttempts: number
  retryBaseDelayMs: number
  retryMaxDelayMs: number
}

export interface UploadFailure {
  kind: FailureKind | 'cancelled'
  retryable: boolean
  /** An authorization failure: stop the whole batch, never bypass auth. */
  halt: boolean
  detail?: string
}

/**
 * Retry only "the server/edge dropped this one request" signals. Never 401/403
 * / validation / oversize / unsupported type.
 */
export function classifyUploadFailure(err: unknown): UploadFailure {
  if (isCancelledError(err)) return { kind: 'cancelled', retryable: false, halt: false }
  const e = (err ?? {}) as { status?: number; message?: string; source?: string; retryAfterSeconds?: number }
  const status = typeof e.status === 'number' ? e.status : undefined
  if (status === undefined) return { kind: 'network', retryable: true, halt: false }
  if (status === 408 || status === 429 || status === 502 || status === 503 || status === 504) {
    return { kind: 'busy', retryable: true, halt: false }
  }
  // 409 from the upload endpoints = "previous attempt of this file still running".
  if (status === 409) return { kind: 'busy', retryable: true, halt: false }
  if (status === 401) return { kind: 'auth', retryable: false, halt: true }
  // 403 from OUR API = the Workspace/role no longer permits uploads. A 403 from
  // object storage (expired signature) is handled inside the transport and, if
  // it still fails, is just this file's rejection - not a batch-wide auth halt.
  if (status === 403 && e.source !== 'storage') return { kind: 'auth', retryable: false, halt: true }
  const msg = (e.message || '').trim()
  return { kind: 'rejected', retryable: false, halt: false, detail: msg || undefined }
}

export function computeBackoffMs(
  attempt: number,
  base: number,
  max: number,
  retryAfterSeconds?: number,
  jitter = 0,
): number {
  const exp = Math.min(base * 2 ** (attempt - 1), max)
  const hinted = retryAfterSeconds && retryAfterSeconds > 0 ? Math.min(retryAfterSeconds * 1000, max) : 0
  return Math.max(exp, hinted) + Math.floor(jitter * 250)
}

function summarize(items: Array<{ status: UploadItemStatus; progress: number }>): BatchCounts {
  let done = 0
  let failed = 0
  let cancelled = 0
  let uploading = 0
  let queued = 0
  let partial = 0
  for (const it of items) {
    if (it.status === 'done') done += 1
    else if (it.status === 'failed') failed += 1
    else if (it.status === 'cancelled') cancelled += 1
    else if (it.status === 'queued') queued += 1
    else {
      uploading += 1
      partial += Math.min(100, Math.max(0, it.progress)) / 100
    }
  }
  const total = items.length
  const percent = total === 0 ? 0 : Math.min(100, Math.floor(((done + failed + cancelled + partial) / total) * 100))
  return { total, done, failed, cancelled, uploading, queued, remaining: queued + uploading, percent }
}

export class UploadManager {
  private batches: InternalBatch[] = []
  private active = 0
  private concurrency = 3
  private snapshot: UploadSnapshot = { version: 0, batches: [] }
  private readonly listeners = new Set<() => void>()
  private readonly eventListeners = new Set<(e: UploadEvent) => void>()
  private emitTimer: ReturnType<typeof setTimeout> | null = null
  private pendingEvents: UploadEvent[] = []
  private wasActive = false
  private readonly now: () => number
  private readonly sleep: (ms: number, signal?: AbortSignal) => Promise<void>
  private readonly random: () => number
  private readonly newId: () => string

  private readonly deps: ManagerDeps

  constructor(deps: ManagerDeps) {
    this.deps = deps
    this.now = deps.now ?? (() => Date.now())
    this.sleep = deps.sleep ?? abortableSleep
    this.random = deps.random ?? Math.random
    this.newId =
      deps.newId ??
      (() =>
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
              const r = Math.floor(Math.random() * 16)
              return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
            }))
  }

  // ---- store API (useSyncExternalStore) ----------------------------------
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot = (): UploadSnapshot => this.snapshot

  subscribeEvents(listener: (e: UploadEvent) => void): () => void {
    this.eventListeners.add(listener)
    return () => {
      this.eventListeners.delete(listener)
    }
  }

  // ---- commands ----------------------------------------------------------
  enqueue(input: BatchInput): string {
    const id = this.newId()
    const items: InternalItem[] = input.files.map((f) => ({
      id: this.newId(), // == the client upload id sent on EVERY attempt of this file
      name: f.file.name,
      relativePath: f.relativePath,
      size: f.file.size,
      status: 'queued',
      progress: 0,
      attempts: 0,
      file: f.file,
      mime: f.mime,
      abort: null,
    }))
    this.batches.push({
      id,
      workspaceId: input.workspaceId,
      source: input.source,
      label: input.label,
      placement: { ...input.placement },
      createdAt: this.now(),
      finishedAt: items.length === 0 ? this.now() : null,
      cancelled: false,
      haltedReason: null,
      skippedCount: input.skippedCount ?? 0,
      items,
      autoProcess: input.autoProcess,
      assetSource: input.assetSource,
      directToR2: input.directToR2,
      scopeBaseUrl: input.scopeBaseUrl,
      retryMaxAttempts: Math.max(1, input.limits.retryMaxAttempts),
      retryBaseDelayMs: input.limits.retryBaseDelayMs,
      retryMaxDelayMs: input.limits.retryMaxDelayMs,
    })
    this.concurrency = Math.max(1, Math.floor(input.limits.concurrency))
    this.emit()
    this.pump()
    return id
  }

  /** Re-queue only the failed files of a batch (done files are never re-sent). */
  retryFailed(batchId: string): void {
    const batch = this.find(batchId)
    if (!batch) return
    let any = false
    for (const it of batch.items) {
      if (it.status === 'failed' && it.file) {
        it.status = 'queued'
        it.progress = 0
        it.attempts = 0
        it.failureKind = undefined
        it.error = undefined
        any = true
      }
    }
    if (!any) return
    batch.finishedAt = null
    batch.haltedReason = null
    batch.cancelled = false
    this.emit()
    this.pump()
  }

  cancelBatch(batchId: string): void {
    const batch = this.find(batchId)
    if (!batch || batch.finishedAt !== null) return
    batch.cancelled = true
    for (const it of batch.items) {
      if (it.status === 'queued') it.status = 'cancelled'
      else if (it.status === 'uploading' || it.status === 'finalizing') it.abort?.abort()
    }
    this.finishIfSettled(batch)
    this.emit()
  }

  /** Remove a FINISHED batch's record (never an active one). */
  dismissBatch(batchId: string): void {
    const batch = this.find(batchId)
    if (!batch || batch.finishedAt === null) return
    this.batches = this.batches.filter((b) => b.id !== batchId)
    this.emit()
  }

  hasActiveUploads(): boolean {
    return this.batches.some((b) => b.items.some((i) => i.status === 'queued' || i.status === 'uploading' || i.status === 'finalizing'))
  }

  /** Test helper: drop all state. */
  reset(): void {
    for (const b of this.batches) for (const it of b.items) it.abort?.abort()
    this.batches = []
    this.active = 0
    this.emit()
  }

  // ---- scheduling --------------------------------------------------------
  private find(id: string): InternalBatch | undefined {
    return this.batches.find((b) => b.id === id)
  }

  private nextQueued(): { batch: InternalBatch; item: InternalItem } | null {
    for (const batch of this.batches) {
      if (batch.cancelled || batch.haltedReason) continue
      const item = batch.items.find((i) => i.status === 'queued')
      if (item) return { batch, item }
    }
    return null
  }

  private pump(): void {
    while (this.active < this.concurrency) {
      const next = this.nextQueued()
      if (!next) break
      this.active += 1
      next.item.status = 'uploading'
      next.item.abort = new AbortController()
      void this.run(next.batch, next.item).finally(() => {
        this.active -= 1
        this.pump()
      })
    }
    this.notifyActive()
  }

  private async run(batch: InternalBatch, item: InternalItem): Promise<void> {
    const abort = item.abort ?? new AbortController()
    item.abort = abort
    try {
      for (let attempt = 1; attempt <= batch.retryMaxAttempts; attempt += 1) {
        item.attempts = attempt
        item.progress = 0
        item.status = 'uploading'
        item.failureKind = undefined
        item.error = undefined
        this.emit()
        try {
          const file = item.file
          if (!file) throw Object.assign(new Error('missing file'), { status: 400 })
          const res = await this.deps.transport(
            { file, clientId: item.id, name: item.name, mime: item.mime, size: item.size },
            {
              scopeBaseUrl: batch.scopeBaseUrl,
              directToR2: batch.directToR2,
              placement: batch.placement,
              autoProcess: batch.autoProcess,
              assetSource: batch.assetSource,
              signal: abort.signal,
              onProgress: (pct) => {
                if (item.status !== 'uploading') return
                if (pct === item.progress) return
                item.progress = pct
                this.emitSoon()
              },
              onPhase: () => {
                item.status = 'finalizing'
                item.progress = 100
                this.emit()
              },
            },
          )
          item.status = 'done'
          item.progress = 100
          item.chainId = res.chainId
          item.file = null // release the File handle; done files are never re-sent
          this.emit()
          this.emitEvent({ type: 'item-done', workspaceId: batch.workspaceId, batchId: batch.id })
          return
        } catch (err) {
          const failure = classifyUploadFailure(err)
          if (failure.kind === 'cancelled' || abort.signal.aborted) {
            item.status = 'cancelled'
            return
          }
          if (failure.retryable && attempt < batch.retryMaxAttempts) {
            const delay = computeBackoffMs(
              attempt,
              batch.retryBaseDelayMs,
              batch.retryMaxDelayMs,
              (err as { retryAfterSeconds?: number }).retryAfterSeconds,
              this.random(),
            )
            item.progress = 0
            this.emit()
            try {
              await this.sleep(delay, abort.signal)
            } catch {
              item.status = 'cancelled'
              return
            }
            continue
          }
          item.status = 'failed'
          item.failureKind = failure.kind as FailureKind
          item.error = failure.detail
          if (failure.halt) this.halt(batch, 'auth')
          return
        }
      }
    } finally {
      item.abort = null
      this.finishIfSettled(batch)
      this.emit()
    }
  }

  /** Authorization is gone: stop starting anything else in this batch. */
  private halt(batch: InternalBatch, reason: 'auth'): void {
    batch.haltedReason = reason
    for (const it of batch.items) {
      if (it.status === 'queued') {
        it.status = 'failed'
        it.failureKind = 'auth'
      }
    }
  }

  private finishIfSettled(batch: InternalBatch): void {
    if (batch.finishedAt !== null) return
    const open = batch.items.some((i) => i.status === 'queued' || i.status === 'uploading' || i.status === 'finalizing')
    if (open) return
    batch.finishedAt = this.now()
    // Delivered right AFTER the snapshot that shows the batch as finished.
    this.pendingEvents.push({ type: 'batch-finished', workspaceId: batch.workspaceId, batchId: batch.id })
  }

  // ---- emission ----------------------------------------------------------
  private build(): UploadSnapshot {
    return {
      version: this.snapshot.version + 1,
      batches: this.batches.map((b) => {
        const items: UploadItemView[] = b.items.map((i) => ({
          id: i.id,
          name: i.name,
          relativePath: i.relativePath,
          size: i.size,
          status: i.status,
          progress: i.progress,
          attempts: i.attempts,
          failureKind: i.failureKind,
          error: i.error,
          chainId: i.chainId,
        }))
        return {
          id: b.id,
          workspaceId: b.workspaceId,
          source: b.source,
          label: b.label,
          placement: b.placement,
          createdAt: b.createdAt,
          finishedAt: b.finishedAt,
          cancelled: b.cancelled,
          haltedReason: b.haltedReason,
          skippedCount: b.skippedCount,
          items,
          counts: summarize(items),
        }
      }),
    }
  }

  private emit(): void {
    if (this.emitTimer) {
      clearTimeout(this.emitTimer)
      this.emitTimer = null
    }
    this.snapshot = this.build()
    for (const l of [...this.listeners]) l()
    this.notifyActive()
    const events = this.pendingEvents
    this.pendingEvents = []
    for (const e of events) this.emitEvent(e)
  }

  private emitSoon(): void {
    const wait = this.deps.progressEmitMs ?? 150
    if (wait <= 0) {
      this.emit()
      return
    }
    if (this.emitTimer) return
    this.emitTimer = setTimeout(() => {
      this.emitTimer = null
      this.emit()
    }, wait)
  }

  private emitEvent(e: UploadEvent): void {
    for (const l of [...this.eventListeners]) l(e)
  }

  private notifyActive(): void {
    const activeNow = this.hasActiveUploads()
    if (activeNow !== this.wasActive) {
      this.wasActive = activeNow
      this.deps.onActiveChange?.(activeNow)
    }
  }
}
